﻿import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { Avatar } from '../../components/Avatar'
import { type AnexoKind, Composer } from '../../components/Composer'
import { ControleDoBot } from '../../components/ControleDoBot'
import { ConversationList, type ListFilters } from '../../components/ConversationList'
import { AssignSelect, StageSelect } from '../../components/HeaderMenus'
import { IconChevronLeft, IconInfo } from '../../components/icons'
import { LeadPanel } from '../../components/LeadPanel'
import { MessageThread, type ThreadMessage } from '../../components/MessageThread'
import {
  ComposerSkeleton,
  LeadPanelSkeleton,
  ThreadHeaderSkeleton,
  ThreadSkeleton,
} from '../../components/Skeletons'
import { useSession } from '../../lib/auth-client'
import { fullName } from '../../lib/format'
import { t } from '../../lib/i18n'
import { inboxColorMap } from '../../lib/palette'
import { useTRPC } from '../../lib/trpc'

/**
 * Duração da entrada e saída dos painéis do celular, em ms.
 *
 * Mora aqui e é interpolada no CSS lá embaixo porque o botão de voltar
 * também depende dela: ele espera esse tempo antes de limpar o `?c=`.
 */
const DURACAO_PAINEL = 220

/**
 * Rótulo curto da mensagem citada, para a faixa acima do composer. Função e
 * não constante de módulo: traduzido no topo do arquivo, ficaria preso no
 * idioma com que o app abriu.
 */
function rotuloDoTipoCitado(kind: string): string | undefined {
  const rotulos: Record<string, string> = {
    photo: t('Foto'),
    video: t('Vídeo'),
    video_note: t('Vídeo circular'),
    voice: t('Áudio'),
    audio: t('Áudio'),
    document: t('Arquivo'),
    sticker: t('Figurinha'),
    animation: 'GIF',
  }
  return rotulos[kind]
}

function previewDaCitacao(message: ThreadMessage): string {
  const texto = (message.text ?? message.caption)?.trim()
  return texto || rotuloDoTipoCitado(message.kind) || t('Mensagem')
}

export const Route = createFileRoute('/_app/inbox')({
  component: Inbox,
  validateSearch: z.object({ c: z.string().optional() }),
})

function Inbox() {
  const trpc = useTRPC()
  const navigate = useNavigate({ from: '/inbox' })
  const queryClient = useQueryClient()
  const session = useSession()
  const { c: selectedId } = Route.useSearch()

  const [filters, setFilters] = useState<ListFilters>({})
  const [profileOpen, setProfileOpen] = useState(false)
  const [replyTo, setReplyTo] = useState<ThreadMessage | null>(null)
  const [fechando, setFechando] = useState(false)
  const fechamento = useRef(0)
  /**
   * Quantas estavam sem ler quando a conversa foi aberta.
   *
   * Guardado aqui porque abrir a conversa chama `markRead` na mesma hora: a
   * partir do primeiro refetch o servidor responde zero, e a marca de onde a
   * leitura parou sumiria da tela junto.
   */
  const [naoLidasAoAbrir, setNaoLidasAoAbrir] = useState(0)
  const contagemCapturada = useRef<string | null>(null)

  const inboxes = useQuery(trpc.inbox.list.queryOptions())
  /**
   * A lista chega em páginas de 25 e o resto vem rolando.
   *
   * `maxPages` existe por causa da invalidação: toda mensagem enviada invalida
   * `conversation`, e o TanStack rebusca **todas** as páginas carregadas. Sem
   * teto, quem rolasse fundo pagaria essa conta no D1 a cada envio.
   */
  const list = useInfiniteQuery({
    ...trpc.conversation.list.infiniteQueryOptions(filters, {
      getNextPageParam: (ultima) => ultima.nextCursor ?? undefined,
    }),
    maxPages: 4,
  })

  // Desduplica por id: a paginação é por deslocamento, então uma mensagem que
  // chega no meio da rolagem pode empurrar uma linha para a página seguinte.
  const conversas = useMemo(() => {
    const vistos = new Set<string>()
    const saida: NonNullable<typeof list.data>['pages'][number]['items'] = []
    for (const pagina of list.data?.pages ?? []) {
      for (const item of pagina.items) {
        if (vistos.has(item.id)) continue
        vistos.add(item.id)
        saida.push(item)
      }
    }
    return saida
  }, [list.data])
  const members = useQuery(trpc.workspace.members.queryOptions())
  const campaignList = useQuery(trpc.campaign.list.queryOptions({}))

  const detail = useQuery({
    ...trpc.conversation.get.queryOptions({ conversationId: selectedId ?? '' }),
    enabled: Boolean(selectedId),
  })
  // Paginação para trás: cada "carregar anteriores" busca uma página mais
  // antiga e empilha na frente. O cursor volta a zero ao trocar de conversa.
  const [olderCursor, setOlderCursor] = useState<number | null>(null)
  const [olderPages, setOlderPages] = useState<ThreadMessage[][]>([])

  // Ajuste durante a render em vez de efeito: trocar de conversa zera o
  // acúmulo antes de pintar, sem exibir o thread antigo por um frame.
  const [pagedConversation, setPagedConversation] = useState(selectedId)
  if (selectedId !== pagedConversation) {
    setPagedConversation(selectedId)
    setOlderCursor(null)
    setOlderPages([])
    // Sem isto a citação atravessaria a troca de conversa e o envio iria
    // referenciar uma mensagem de outro chat.
    setReplyTo(null)
    // Trocar de conversa — ou o `?c=` sumir por fora, pelo gesto de voltar do
    // aparelho — cancela um fechamento em andamento: senão o timer dispararia
    // depois e limparia a conversa que acabou de ser aberta.
    window.clearTimeout(fechamento.current)
    setFechando(false)
  }

  // Uma captura por conversa: os refetches seguintes já vêm zerados pelo
  // `markRead`, e sobrescrever apagaria a marca de onde a leitura parou.
  if (detail.data && contagemCapturada.current !== detail.data.id) {
    contagemCapturada.current = detail.data.id
    setNaoLidasAoAbrir(detail.data.unreadCount)
  }

  const thread = useQuery({
    ...trpc.conversation.messages.queryOptions({ conversationId: selectedId ?? '' }),
    enabled: Boolean(selectedId),
  })

  const olderPage = useQuery({
    ...trpc.conversation.messages.queryOptions({
      conversationId: selectedId ?? '',
      cursor: olderCursor ?? undefined,
    }),
    enabled: Boolean(selectedId && olderCursor),
  })

  // Acumula a página recém-chegada sem repetir: o efeito roda de novo a cada
  // render, mas a chave é o cursor que a originou.
  useEffect(() => {
    if (!olderCursor || !olderPage.data) return
    setOlderPages((pages) =>
      pages.some((page) => page[0]?.id === olderPage.data.items[0]?.id)
        ? pages
        : [olderPage.data.items, ...pages],
    )
  }, [olderCursor, olderPage.data])

  const messages = [...olderPages.flat(), ...(thread.data?.items ?? [])]

  // Enquanto houver página carregada, o "mais antigo" é dela; senão, o da
  // primeira consulta.
  const oldestCursor = olderPages.length
    ? (olderPage.data?.nextCursor ?? null)
    : (thread.data?.nextCursor ?? null)
  const stages = useQuery({
    ...trpc.inbox.stages.queryOptions({ inboxId: detail.data?.inboxId ?? '' }),
    enabled: Boolean(detail.data?.inboxId),
  })
  const quickReplies = useQuery({
    ...trpc.quickReply.list.queryOptions({ inboxId: detail.data?.inboxId ?? '' }),
    enabled: Boolean(detail.data?.inboxId),
  })
  const scheduled = useQuery({
    ...trpc.conversation.scheduled.queryOptions({ conversationId: selectedId ?? '' }),
    enabled: Boolean(selectedId),
  })
  /**
   * Timeline em páginas: abrir a conversa traz só a primeira.
   *
   * Um lead antigo tem centenas de entradas, e abrir o perfil é o gesto mais
   * repetido do dia — trazer tudo sempre era leitura jogada fora em quase
   * todas as vezes.
   */
  const leadActivity = useInfiniteQuery({
    ...trpc.lead.activity.infiniteQueryOptions(
      { leadId: detail.data?.leadId ?? '' },
      { getNextPageParam: (ultima) => ultima.nextCursor ?? undefined },
    ),
    enabled: Boolean(detail.data?.leadId),
  })

  const atividades = (leadActivity.data?.pages ?? []).flatMap((pagina) => pagina.items)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [['conversation']] })
    queryClient.invalidateQueries({ queryKey: [['lead']] })
  }

  const markRead = useMutation(trpc.conversation.markRead.mutationOptions())
  /**
   * Aviso efêmero: não invalida nada e nada na tela muda quando falha.
   *
   * Mas o motivo vai para o console. Sem isso, "o digitando não apareceu" não
   * tem como ser diagnosticado de fora — foi exatamente o que aconteceu.
   */
  const digitando = useMutation({
    ...trpc.conversation.typing.mutationOptions(),
    onSuccess: (resultado) => {
      if (!resultado.ok) console.warn('[digitando] recusado:', resultado.erro)
    },
    onError: (erro) => console.warn('[digitando] falhou:', erro.message),
  })
  const togglePin = useMutation({
    ...trpc.conversation.togglePin.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['conversation', 'list']] }),
  })
  const send = useMutation({ ...trpc.conversation.send.mutationOptions(), onSuccess: invalidate })
  const addNote = useMutation({
    ...trpc.conversation.addNote.mutationOptions(),
    onSuccess: invalidate,
  })
  const setStage = useMutation({
    ...trpc.conversation.setStage.mutationOptions(),
    onSuccess: invalidate,
  })
  const assign = useMutation({
    ...trpc.conversation.assign.mutationOptions(),
    onSuccess: invalidate,
  })
  const cancelSchedule = useMutation({
    ...trpc.conversation.cancelSchedule.mutationOptions(),
    onSuccess: invalidate,
  })
  const schedule = useMutation({
    ...trpc.conversation.schedule.mutationOptions(),
    onSuccess: invalidate,
  })
  const sendMedia = useMutation({
    ...trpc.conversation.sendMedia.mutationOptions(),
    onSuccess: invalidate,
  })

  /**
   * Sobe o arquivo e só então cria a mensagem. Duas etapas de propósito: se o
   * upload falhar, não fica mensagem órfã no thread.
   */
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  async function attach(file: File, caption?: string) {
    if (!selectedId) return
    setUploading(true)
    setUploadError(null)

    try {
      const form = new FormData()
      form.set('file', file)
      form.set('conversationId', selectedId)

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
      const result = (await response.json()) as { mediaId?: string; kind?: string; error?: string }

      if (!response.ok || !result.mediaId) {
        setUploadError(result.error ?? 'Falha ao enviar o arquivo')
        return
      }

      sendMedia.mutate({
        conversationId: selectedId,
        anexos: [{ mediaId: result.mediaId, kind: result.kind === 'photo' ? 'photo' : 'document' }],
        ...(caption ? { caption } : {}),
      })
    } catch {
      setUploadError('Falha de rede ao enviar o arquivo')
    } finally {
      setUploading(false)
    }
  }

  /**
   * Nota de voz gravada no composer: sobe e sai numa ação só.
   *
   * A duração vai junto porque o Worker não tem como medir sem decodificar o
   * áudio — e sem ela o Telegram mostra a nota com 0:00.
   */
  async function enviarAudio(audio: {
    blob: Blob
    fileName: string
    duracao: number
    kind: 'voice' | 'audio'
  }) {
    if (!selectedId) return
    setUploading(true)
    setUploadError(null)

    try {
      const form = new FormData()
      form.set('file', new File([audio.blob], audio.fileName, { type: audio.blob.type }))
      form.set('conversationId', selectedId)
      form.set('duration', String(audio.duracao))
      // O servidor só aceita a intenção se o formato permitir nota de voz.
      form.set('kind', audio.kind)

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
      const result = (await response.json()) as { mediaId?: string; kind?: string; error?: string }

      if (!response.ok || !result.mediaId) {
        setUploadError(result.error ?? t('Falha ao enviar o áudio'))
        return
      }

      sendMedia.mutate({
        conversationId: selectedId,
        // O servidor reclassifica pelo mime; o gravador só dá a intenção.
        anexos: [
          { mediaId: result.mediaId, kind: (result.kind as 'voice' | 'audio') ?? audio.kind },
        ],
      })
    } catch {
      setUploadError(t('Falha de rede ao enviar o áudio'))
    } finally {
      setUploading(false)
    }
  }

  /**
   * Anexos de resposta rápida: já estão no R2, então pulam o upload.
   *
   * Lista porque a resposta pode levar um álbum. Com dois ou mais o servidor
   * manda tudo numa mensagem só; com um, pelo método do próprio tipo.
   */
  function enviarAnexoPronto(
    anexos: Array<{ mediaId: string; kind: AnexoKind }>,
    caption?: string,
  ) {
    if (!selectedId || anexos.length === 0) return
    sendMedia.mutate({
      conversationId: selectedId,
      anexos,
      ...(caption ? { caption } : {}),
    })
  }

  const workspaceTags = useQuery(trpc.lead.tags.queryOptions())
  const channels = useQuery(trpc.channel.list.queryOptions())

  // As ações da lista — do menu da linha e da seleção múltipla. A mesma
  // mutação serve aos dois: uma conversa é um array de um.
  const markAnswered = useMutation({
    ...trpc.conversation.markAnswered.mutationOptions(),
    onSuccess: invalidate,
  })
  const markUnread = useMutation({
    ...trpc.conversation.markUnread.mutationOptions(),
    onSuccess: invalidate,
  })
  const react = useMutation({ ...trpc.conversation.react.mutationOptions(), onSuccess: invalidate })
  const closeConversations = useMutation({
    ...trpc.conversation.close.mutationOptions(),
    onSuccess: invalidate,
  })
  const addTag = useMutation({ ...trpc.lead.addTag.mutationOptions(), onSuccess: invalidate })
  const removeTag = useMutation({ ...trpc.lead.removeTag.mutationOptions(), onSuccess: invalidate })

  const scope = useQuery(trpc.session.queryOptions())
  const purge = useMutation({
    ...trpc.lead.purge.mutationOptions(),
    onSuccess: () => {
      // A conversa aberta deixou de existir; voltar para a lista evita uma
      // tela apontando para um id que o servidor já apagou.
      navigate({ search: {} })
      invalidate()
    },
  })

  // Abrir a conversa zera o contador. `mutate` é referencialmente estável no
  // react-query, então dispara só na troca de conversa.
  const markReadMutate = markRead.mutate
  useEffect(() => {
    if (selectedId) markReadMutate({ conversationId: selectedId })
  }, [selectedId, markReadMutate])

  /**
   * Esc fecha a conversa — o mesmo que clicar em Inbox de novo. Só a seleção
   * sai; etapa, responsável e rascunho ficam como estavam.
   *
   * Não dispara quando o Esc pertence a outra coisa: menu aberto, diálogo, ou
   * um campo em que a pessoa está digitando — ali Esc significa "cancela isto
   * aqui", não "sai da conversa".
   */
  useEffect(() => {
    if (!selectedId) return

    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return

      const target = event.target as HTMLElement | null
      if (target?.closest('[role="dialog"], [role="menu"], input, textarea, select')) return

      navigate({ search: {} })
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, navigate])

  // Sair da tela com um fechamento pendente deixaria o timer navegando depois,
  // por cima de onde a pessoa já foi parar.
  useEffect(() => () => window.clearTimeout(fechamento.current), [])

  // Cor de identidade por conta: a escolhida nas configurações, ou a da paleta
  // por posição enquanto ninguém escolheu.
  const colors = useMemo(() => inboxColorMap(inboxes.data ?? []), [inboxes.data])

  const inboxOptions = (inboxes.data ?? []).map((inbox) => ({
    id: inbox.id,
    name: inbox.name,
    color: colors.get(inbox.id) ?? 'var(--line-strong)',
  }))

  function selectConversation(id: string) {
    navigate({ search: { c: id } })
    setProfileOpen(false)
  }

  /**
   * Voltar para a lista espera a animação antes de mexer na URL.
   *
   * Limpando o `?c=` na hora, a conversa vira "Selecione uma conversa" já no
   * primeiro quadro e o que desliza para fora da tela é o vazio. Segurando o
   * `?c=` pelos 220ms da transição, quem sai é a conversa que estava aberta.
   */
  function voltarParaLista() {
    setFechando(true)
    fechamento.current = window.setTimeout(() => navigate({ search: {} }), DURACAO_PAINEL)
  }

  const lead = detail.data
  const canReply = lead?.channelType !== 'business' || lead?.rights?.can_reply === true
  const accountColor = lead
    ? (colors.get(lead.inboxId) ?? 'var(--line-strong)')
    : 'var(--line-strong)'

  return (
    <div className="inbox-grid h-full" data-open={selectedId && !fechando ? 'true' : 'false'}>
      {/* `overflow-hidden`: o pane é a moldura da lista, e nada dentro dela
          pode empurrar a página. */}
      <div className="pane-list min-h-0 overflow-hidden">
        <ConversationList
          items={conversas}
          isLoading={list.isPending}
          hasMore={list.hasNextPage}
          isLoadingMore={list.isFetchingNextPage}
          onLoadMore={() => {
            if (list.hasNextPage && !list.isFetchingNextPage) list.fetchNextPage()
          }}
          selectedId={selectedId ?? null}
          onSelect={selectConversation}
          filters={filters}
          onFiltersChange={setFilters}
          inboxes={inboxOptions}
          members={(members.data ?? []).map((m) => ({ userId: m.userId, name: m.name }))}
          campaigns={(campaignList.data ?? []).map((c) => ({ id: c.id, label: c.label }))}
          total={list.data?.pages[0]?.total ?? 0}
          onTogglePin={(conversationId, pinned) => togglePin.mutate({ conversationId, pinned })}
          onMarkAnswered={(conversationIds) => markAnswered.mutate({ conversationIds })}
          onMarkUnread={(conversationIds) => markUnread.mutate({ conversationIds })}
          onClose={(conversationIds) => closeConversations.mutate({ conversationIds })}
          tags={workspaceTags.data ?? []}
          channels={(channels.data ?? []).map((row) => ({
            id: row.id,
            name: row.name,
            color: row.color,
          }))}
        />
      </div>

      <div className="pane-thread flex min-h-0 min-w-0 flex-col bg-paper">
        {!selectedId || detail.isError ? (
          <EmptyThread />
        ) : !lead ? (
          /* A conversa foi escolhida mas o detalhe ainda não chegou — antes
             este intervalo mostrava "Selecione uma conversa", que lia como se
             o clique não tivesse funcionado. */
          <>
            <ThreadHeaderSkeleton />
            <ThreadSkeleton />
            <ComposerSkeleton />
          </>
        ) : (
          <>
            <header className="flex flex-none flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5 max-md:px-2">
              <button
                type="button"
                onClick={voltarParaLista}
                aria-label={t('Voltar para a lista')}
                className="inline-flex size-9 flex-none items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink md:hidden"
              >
                <IconChevronLeft className="size-5" />
              </button>

              <div className="flex min-w-0 items-center gap-3 max-md:flex-1">
                <Avatar
                  leadId={lead.leadId}
                  name={fullName(lead)}
                  hasPhoto={lead.hasPhoto}
                  className="size-9 flex-none rounded-full text-xs ring-2 ring-offset-2 ring-offset-surface"
                  style={{ ['--tw-ring-color' as string]: accountColor }}
                />
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-lg font-bold">{fullName(lead)}</span>
                    {lead.username && (
                      <span className="text-2xs text-ink-3 max-md:hidden">@{lead.username}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-[5px] truncate text-xs text-ink-2">
                    <span
                      aria-hidden
                      className="size-2 flex-none rounded-full"
                      style={{ background: accountColor }}
                    />
                    {t('via {conta}', { conta: lead.channelName })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setProfileOpen((open) => !open)}
                  aria-label={t('Perfil do lead')}
                  className="ml-auto inline-flex size-10 flex-none items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink md:hidden"
                >
                  <IconInfo className="size-[18px]" />
                </button>
              </div>

              {/* No celular etapa e responsável descem para uma linha só
                  deles, centralizada. Antes ficavam alinhados à esquerda com
                  um recuo do tamanho do botão de voltar — só que o botão está
                  na linha de cima, e o recuo puxava o par para fora do eixo. */}
              <div className="relative ml-auto flex items-center gap-2 max-md:ml-0 max-md:basis-full max-md:justify-center max-md:pb-1">
                <StageSelect
                  stages={stages.data ?? []}
                  currentId={lead.stageId}
                  onChange={(stageId) =>
                    selectedId && setStage.mutate({ conversationId: selectedId, stageId })
                  }
                />
                <AssignSelect
                  members={members.data ?? []}
                  currentUserId={session.data?.user.id ?? ''}
                  assigneeUserId={lead.assigneeUserId}
                  onChange={(userId) =>
                    selectedId && assign.mutate({ conversationId: selectedId, userId })
                  }
                />
                <button
                  type="button"
                  onClick={() => setProfileOpen((open) => !open)}
                  aria-label={t('Perfil do lead')}
                  className="inline-flex size-8 flex-none items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink max-md:hidden xl:hidden"
                >
                  <IconInfo className="size-[18px]" />
                </button>
              </div>
            </header>

            <MessageThread
              conversationId={selectedId}
              unreadCount={naoLidasAoAbrir}
              messages={messages}
              scheduled={scheduled.data ?? []}
              isLoading={thread.isPending}
              hasMore={Boolean(oldestCursor)}
              isLoadingMore={olderPage.isFetching}
              onLoadMore={() => oldestCursor && setOlderCursor(oldestCursor)}
              onCancelSchedule={(id) => cancelSchedule.mutate({ scheduledId: id })}
              onReply={setReplyTo}
              /**
               * Ausente em conta business: a Bot API não aceita
               * `business_connection_id` no `setMessageReaction`, então o bot
               * não consegue reagir pela pessoa. Sem a prop o botão nem
               * aparece — oferecer algo que sempre dá erro é pior do que não
               * oferecer.
               */
              {...(lead.channelType === 'business'
                ? {}
                : {
                    onReact: (messageId: string, emoji: string | null) =>
                      react.mutate({ messageId, emoji: emoji as never }),
                  })}
              cancelingId={cancelSchedule.isPending ? cancelSchedule.variables.scheduledId : null}
            />

            {/* Acima do campo de mensagem de propósito: quem vai responder
                precisa saber que existe um robô a caminho antes de escrever. */}
            {selectedId && <ControleDoBot conversationId={selectedId} />}

            {uploadError && (
              <p role="alert" className="flex-none bg-danger-soft px-4 py-2 text-2xs text-danger">
                {uploadError}
              </p>
            )}

            <Composer
              contato={
                detail.data
                  ? {
                      firstName: detail.data.firstName,
                      lastName: detail.data.lastName,
                      username: detail.data.username,
                    }
                  : undefined
              }
              canReply={canReply}
              isSending={send.isPending || addNote.isPending}
              isUploading={uploading || sendMedia.isPending}
              onAttach={attach}
              onSendMedia={enviarAnexoPronto}
              onDigitando={
                selectedId ? () => digitando.mutate({ conversationId: selectedId }) : undefined
              }
              onSendAudio={enviarAudio}
              quickReplies={quickReplies.data ?? []}
              replyTo={
                replyTo?.telegramMessageId
                  ? {
                      telegramMessageId: replyTo.telegramMessageId,
                      preview: previewDaCitacao(replyTo),
                      fromLead: replyTo.direction === 'in',
                    }
                  : null
              }
              onClearReply={() => setReplyTo(null)}
              onSend={(text) => {
                send.mutate({
                  conversationId: selectedId,
                  text,
                  ...(replyTo?.telegramMessageId
                    ? { replyToTelegramMessageId: replyTo.telegramMessageId }
                    : {}),
                })
                setReplyTo(null)
              }}
              onAddNote={(text) => addNote.mutate({ conversationId: selectedId, text })}
              onSchedule={(text, sendAt) =>
                schedule.mutate({ conversationId: selectedId, text, sendAt })
              }
            />
          </>
        )}
      </div>

      {selectedId && !detail.isError && (
        <div className="pane-profile min-h-0" data-open={profileOpen ? 'true' : 'false'}>
          {!lead ? (
            <LeadPanelSkeleton />
          ) : (
            <LeadPanel
              lead={lead}
              accountColor={accountColor}
              activity={atividades}
              temMaisAtividade={leadActivity.hasNextPage}
              carregandoAtividade={leadActivity.isFetchingNextPage}
              onVerMaisAtividade={() => leadActivity.fetchNextPage()}
              knownTags={workspaceTags.data ?? []}
              canPurge={scope.data?.isAdmin ?? false}
              isPurging={purge.isPending}
              onOpenConversation={selectConversation}
              onAddTag={(label) => addTag.mutate({ leadId: lead.leadId, label })}
              onRemoveTag={(tagId) => removeTag.mutate({ leadId: lead.leadId, tagId })}
              onPurge={() => purge.mutate({ leadId: lead.leadId, confirm: true })}
            />
          )}
        </div>
      )}

      {/* Montado sempre, para escurecer junto com o painel em vez de piscar. */}
      <button
        type="button"
        aria-label={t('Fechar perfil')}
        tabIndex={profileOpen ? 0 : -1}
        aria-hidden={!profileOpen}
        onClick={() => setProfileOpen(false)}
        className={`fixed inset-x-0 top-0 z-[75] h-[var(--altura-janela,100%)] bg-black/25 transition-opacity duration-200 motion-reduce:transition-none xl:hidden ${
          profileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <style>{`
        /* Abaixo de md a lista e a conversa se revezam no mesmo espaço; de md
           a xl o perfil vira painel sobreposto; acima de xl são 3 colunas. */
        .inbox-grid {
          display: grid;
          grid-template-columns: 1fr;
          /* Uma linha só, presa à altura do container: com 'auto' a lista
             cresceria com o conteúdo e estouraria a tela. */
          grid-template-rows: minmax(0, 1fr);
        }

        /**
         * No celular a conversa é uma camada por cima de tudo, inclusive da
         * barra do app — dentro de um chat ela não serve para nada, quem volta
         * é a seta do próprio cabeçalho da conversa.
         *
         * Antes lista e conversa trocavam por 'display: none': não havia como
         * animar, e o corte seco ao voltar para o inbox lia como travada.
         * Sendo 'fixed', a conversa desliza sem empurrar nada e a lista atrás
         * fica parada no lugar em que estava.
         */
        @media (max-width: 767px) {
          .inbox-grid > .pane-thread {
            position: fixed;
            inset: 0 0 auto 0;
            /* Não 'bottom: 0': no PWA do iOS o viewport de layout nasce mais
               curto que a tela, e a conversa terminava antes da borda — o
               composer ficava com uma faixa da cor do 'paper' embaixo. A
               medida vem de 'main.tsx'; sem ela, 100% é o de sempre. */
            height: var(--altura-janela, 100%);
            z-index: 70;
            translate: 100% 0;
            visibility: hidden;
            transition: translate ${DURACAO_PAINEL}ms ease, visibility 0s ${DURACAO_PAINEL}ms;
            padding-left: env(safe-area-inset-left);
            padding-right: env(safe-area-inset-right);
          }
          .inbox-grid[data-open='true'] > .pane-thread {
            translate: 0 0;
            visibility: visible;
            transition: translate ${DURACAO_PAINEL}ms ease, visibility 0s;
          }

          /* Cobrindo a barra do app, é o cabeçalho da conversa que passa a
             encostar no relógio — então o recorte do topo é reservado nele,
             para o fundo dele subir até lá. Regra fora de camada: ela precisa
             ganhar do 'py-2.5' do próprio cabeçalho. */
          .pane-thread > header {
            position: relative;
            padding-top: calc(env(safe-area-inset-top) + 10px);
          }

          /* Faixa do relógio no tema claro, igual à '.barra-mobile::before':
             o iOS desenha o relógio sempre em branco, e sobre o cabeçalho
             claro da conversa ele sumia. No escuro a faixa herda o cabeçalho. */
          .pane-thread > header::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: env(safe-area-inset-top);
            background: #0a0a0f;
          }
          .dark .pane-thread > header::before {
            background: var(--surface);
          }

          /* Atrás da conversa a lista continua existindo: sem isto ela seguiria
             recebendo foco e leitor de tela por baixo. Some no fim da entrada e
             volta no começo da saída, daí o atraso em um lado só. */
          .inbox-grid[data-open='true'] > .pane-list {
            visibility: hidden;
            transition: visibility 0s ${DURACAO_PAINEL}ms;
          }
        }

        /* Nada reserva a base (decisão do dono): composer, gaveta e o fim da
           lista encostam na borda de baixo. A reserva no fim da lista fazia a
           última conversa parar antes da borda, como se outro bloco começasse
           ali.

           A lista alcança a borda porque a casca mede a janela real (a
           propriedade --altura-janela, definida em main.tsx), e não porque
           transborda o pane — o transbordo era o contorno antigo e foi
           removido: ele fazia o documento inteiro rolar por trás da conversa
           aberta no PWA.

           Sem crase neste bloco: ele vive dentro do template literal do
           <style>, e uma crase aqui fecha a string. */

        @media (min-width: 768px) {
          .inbox-grid { grid-template-columns: 336px minmax(0, 1fr); }
          .inbox-grid .pane-list { display: flex; }
        }
        @media (min-width: 1280px) {
          .inbox-grid[data-open='true'] { grid-template-columns: 336px minmax(0, 1fr) 320px; }
        }

        /* Até xl o perfil é painel sobreposto. Sendo 'fixed', o recuo da casca
           não alcança ele: encostava no relógio. O fundo fica no próprio
           painel para a faixa reservada sair da mesma cor do conteúdo. A base
           não entra aqui: quem reserva é o miolo que rola do LeadPanel, que
           também cobre o painel em fluxo do xl. */
        @media (max-width: 1279px) {
          .pane-profile {
            position: fixed;
            inset: 0 0 auto auto;
            height: var(--altura-janela, 100%);
            z-index: 80;
            width: 320px;
            max-width: 88vw;
            background: var(--surface);
            padding-top: env(safe-area-inset-top);
            padding-right: env(safe-area-inset-right);
            translate: 100% 0;
            visibility: hidden;
            transition: translate ${DURACAO_PAINEL}ms ease, visibility 0s ${DURACAO_PAINEL}ms;
          }
          .pane-profile[data-open='true'] {
            translate: 0 0;
            visibility: visible;
            box-shadow: var(--shadow-pop);
            transition: translate ${DURACAO_PAINEL}ms ease, visibility 0s;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .inbox-grid > .pane-list,
          .inbox-grid > .pane-thread,
          .pane-profile { transition: none; }
        }
      `}</style>
    </div>
  )
}

function EmptyThread() {
  return (
    <div className="grid h-full place-items-center px-6 text-center">
      <div className="max-w-sm">
        <p className="font-display text-xl">{t('Selecione uma conversa')}</p>
        {/* "Bot API" saiu do texto visível: quem atende não precisa saber o
            nome da interface do Telegram para entender que o histórico
            anterior não vem. */}
        <p className="mt-2 text-xs leading-relaxed text-ink-2">
          {t(
            'O CRM mostra apenas mensagens recebidas depois que a conta do Telegram foi conectada. Conversas anteriores não são importadas — o Telegram não dá acesso ao histórico.',
          )}
        </p>
      </div>
    </div>
  )
}
